import { round } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'
import i18n from 'simple-react-i18n'
import { getFullDate, getFullDateMini } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { CAMPAIGNS, DESCRIPTION, EVENTS } from '../../constants/AlertConstants'
import { shallowEqual, useSelector } from 'react-redux'
import { Grid, Icon, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { selectedColor } from 'utils/constants/ColorTheme'

const useStyles = makeStyles(() => ({
    bold: {
        fontSize: '14px',
        fontWeight: 'bold',
    },
}))

const AlertSitePanel = ({
    site = {},
    measureDate = '',
    onClick = () => {},
    onToggle,
    onHover = () => {},
    withoutIcon = false,
    title = '',
    detailed = true,
}) => {
    const classes = useStyles()

    const {
        cardMarkerIcon = '',
        markerIcon = '',
        markerName = '',
        icon = '',
        tooltip = '',
        name = '',
        code = '',
        cityName = '',
        lastMeasure = [],
        typeName = '',
        color = 'white',
    } = site

    const cardIcon = cardMarkerIcon || markerIcon
    const lastMeasuresFiltered = lastMeasure.filter(m => hasValue(m?.value))

    const { piezometryDataTypes, pluviometryDataTypes, hydrometryDataTypes, citiesIndex } = useSelector(store => ({
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const getLastMeasureUnit = (type, lastMeasureValue) => {
        const units = {
            pluviometry: pluviometryDataTypes,
            hydrometry: hydrometryDataTypes,
            piezometry: piezometryDataTypes,
        }

        const typeUnit = units[type]
        const dataType = type === 'piezometry' ? -1 : parseInt(lastMeasureValue?.dataType)
        const unit = typeUnit?.find(u => parseInt(u.id) === dataType)?.unit

        return unit?.toString().replaceAll('[', '').replaceAll(']', '') ?? ''
    }

    const isContact = ['contact', 'contributor'].includes(typeName)

    const city = citiesIndex[site?.townCode]

    return (
        <Grid
            container
            sx={{ cursor: 'pointer', border: !withoutIcon && `solid 3px ${color}`, borderRadius: !withoutIcon && '5px', overflow: 'hidden' }}
            onMouseOver={() => onHover(site, true)}
            onMouseOut={() => onHover(site, false)}
        >
            <Grid
                container size={12}
                alignItems='center'
                onClick={() => {
                    if (onToggle) {
                        onToggle()
                    } else {
                        onClick(site, DESCRIPTION)
                    }
                }}
            >
                {!withoutIcon && (
                    <Grid container size={1} alignItems='center' sx={{ height: '100%', backgroundColor: color }}>
                        <Grid size={12} sx={{ '& img': { height: cardMarkerIcon ? '30px' : '40px' }, textAlign: 'center' }}>
                            <Tooltip title={markerName} placement='top'>
                                <img
                                    src={
                                        cardIcon.startsWith('<svg') ?
                                            `data:image/svg+xml;utf8,${cardIcon}`
                                            :
                                            cardIcon
                                    }
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                )}
                <Grid container size={withoutIcon ? 12 : 11}>
                    <Grid container size={12} sx={{ paddingLeft: '0.75rem' }}>
                        <Grid container size={12}>
                            {isContact && (
                                <Grid size={12}>
                                    {site.name ?? ''}
                                </Grid>
                            )}
                            <Grid container size={12}>
                                {isContact ? (
                                    <Grid size={12}>
                                        {`${site.road ?? ''}${site.postalCode ? `, ${site.postalCode}` : ''}${site.city ? `, ${site.city}` : ''}`}
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid size={12} className={classes.bold}>
                                            {`${code} ${name ? `- ${name}` : ''}`}
                                        </Grid>
                                        <Grid size={12}>
                                            {`${cityName ? `${cityName} -` : city?.name ? `${city?.name} -` : ''} ${site.townCode ?? ''}`}
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        {!!(!isContact && detailed) && (
                            <Grid container size={12}>
                                {typeName === STATION_TYPE_NAME.quality ? (
                                    (site.historyStart && site.historyEnd) ? (
                                        <Grid container size={12}>
                                            <Grid size={12}>
                                                <b>{i18n.historyDate}</b>
                                            </Grid>
                                            <Grid container size={12}>
                                                <Grid sx={{ paddingRight: '5px' }}>
                                                    {getFullDateMini(site.historyStart)}
                                                </Grid>
                                                <Grid sx={{ paddingRight: '5px' }}>
                                                    {i18n.to}
                                                </Grid>
                                                <Grid sx={{ paddingRight: '5px' }}>
                                                    {getFullDateMini(site.historyEnd)}
                                                </Grid>
                                            </Grid>
                                            <Grid size={12}>
                                                <b>{i18n.lastOperation2}</b>
                                            </Grid>
                                            <Grid container size={12}>
                                                <Grid sx={{ paddingRight: '5px' }}>
                                                    {getFullDateMini(site.historyEnd)}
                                                </Grid>
                                                <Grid>
                                                    {site.operationCount ?? 0} {site.operationCount && site.operationCount.length > 1 ? i18n.analyses : i18n.analysis}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Grid>
                                            {i18n.noValue}
                                        </Grid>
                                    )
                                ) : (
                                    <Grid container size={12}>
                                        <Grid size={12}>
                                            {`${site.creationDate ? `${moment(site.creationDate).year()} -` : ''} ${site.measureDate?.year() ?? ''}`}
                                        </Grid>
                                        {lastMeasuresFiltered.length ? lastMeasuresFiltered.map(m => (
                                            <Grid container size={12} key={m.measureDate}>
                                                {(m.title || title) && (
                                                    <Grid sx={{ paddingRight: '5px' }}>
                                                        {m.title || title} :
                                                    </Grid>
                                                )}
                                                <Grid sx={{ paddingRight: '5px' }}>
                                                    {getFullDate(moment(m.measureDate || m.date)) || measureDate} -
                                                </Grid>
                                                <Grid>
                                                    {`${round(m.value, 3)} ${m?.unit || getLastMeasureUnit(typeName, m)}`}
                                                </Grid>
                                            </Grid>
                                        )) : (
                                            <Grid size={12}>
                                                {i18n.noValue}
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                    {!!detailed && (
                        <Grid container size={12}>
                            {!!icon.leftIcon && (
                                <Grid container size={12} sx={{ padding: '0 10px 2px' }}>
                                    <Grid>
                                        <Tooltip title={tooltip}>
                                            <Icon
                                                size={20}
                                                sx={{ color: icon.leftIconColor }}
                                            >
                                                {icon.leftIcon}
                                            </Icon>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid container size={12} justifyContent='flex-end' sx={{ backgroundColor: selectedColor, padding: '2px 10px', marginTop: icon.leftIcon ? '0.5rem' : undefined, gap: '0.5rem' }}>
                                {hasValue(site.jobExecutionId) && (
                                    <Grid>
                                        <Tooltip title={i18n.openData}>
                                            <Icon size={20}>wifi</Icon>
                                        </Tooltip>
                                    </Grid>
                                )}
                                {site.bookmarked && (
                                    <Grid>
                                        <Tooltip title={i18n.bookmark}>
                                            <Icon size={20}>
                                                grade
                                            </Icon>
                                        </Tooltip>
                                    </Grid>
                                )}
                                {(site.currentComments || site.currentMonitoring) && (
                                    <Grid>
                                        <Tooltip title={i18n.currentEvents}>
                                            <Icon size={20} onClick={() => onClick(site, EVENTS, 'date')}>
                                                event
                                            </Icon>
                                        </Tooltip>
                                    </Grid>
                                )}
                                {hasValue(site.currentMonitoring) && (
                                    <Grid>
                                        <Tooltip title={i18n.oversight}>
                                            <Icon
                                                size={20}
                                                onClick={() => onClick(site, EVENTS, 'eventType')}
                                            >
                                                notifications
                                            </Icon>
                                        </Tooltip>
                                    </Grid>
                                )}
                                {hasValue(site.currentCampaign) && (
                                    <Grid>
                                        <Tooltip title={i18n.currentCampaign}>
                                            <Icon
                                                size={20}
                                                onClick={() => onClick(site, CAMPAIGNS)}
                                            >
                                                nature_people
                                            </Icon>
                                        </Tooltip>
                                    </Grid>
                                )}
                                <Grid>
                                    <Tooltip title={i18n.description}>
                                        <Icon
                                            size={20}
                                            onClick={() => onClick(site, DESCRIPTION)}
                                        >
                                            info
                                        </Icon>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}

AlertSitePanel.propTypes = {
    measureDate: PropTypes.string,
    onClick: PropTypes.func,
    onToggle: PropTypes.func,
    site: PropTypes.shape({}),
    onHover: PropTypes.func,
    message: PropTypes.string,
    withoutIcon: PropTypes.boolean,
    unit: PropTypes.string,
    title: PropTypes.string,
    detailed: PropTypes.boolean,
}

export default AlertSitePanel